import { useMutation } from '@apollo/client';
import { Controller, useForm } from 'react-hook-form';

import {
	Button,
	Grid,
	GridCol,
	Input,
	Popup,
	PopupSection,
	Text,
	Textarea,
} from '@flixbus/honeycomb-react';

import { DEFAULT_FROM_TIME_VALUE } from 'common/activeElements/modals/hooks/const';
import LocationSearch from 'common/LocationSearch';
import { standardError, standardSuccess } from 'global/commonMessages';
import { notificationStore } from 'global/NotificationStore';
import { resetTimestampToUtc, updateDateWithTime } from 'utils/datetime';

import { ADD_LOCATION_CHANGE_SUCCESS_RESPONSE, DEFAULT_VALUES } from './const';
import { ADD_CREW_MEMBER_LOCATION_CHANGE } from './qql/addLocationChange';
import { MODAL_UPDATE_CREW_LOCATION_TEXT } from './text';

import styles from './styles.module.css';

/**
 * PROPS (ActiveElements + page):
 * - employeeId: string (required)
 * - onClose: () => void
 * - onActionSuccess?: () => void
 * - initialData?: { cityId?, from?, fromTime?, note? }   // optional prefill
 */
const ModalUpdateCrewLocation = ({
	employeeId,
	onClose,
	onActionSuccess = () => {},
	initialData = DEFAULT_VALUES,
}) => {
	const {
		control,
		getValues,
		handleSubmit,
		setValue,
		reset,
		watch,
		formState: { dirtyFields, isValid },
	} = useForm({
		mode: 'onChange',
		defaultValues: initialData,
	});

	const [addLocationChange, { loading }] = useMutation(ADD_CREW_MEMBER_LOCATION_CHANGE, {
		onCompleted: (data) => {
			const response = data?.addLocationChange;
			if (response?.__typename === ADD_LOCATION_CHANGE_SUCCESS_RESPONSE) {
				handleResetForm();
				notificationStore.setNotification(standardSuccess);
				onActionSuccess();
				onClose();
			}
		},
		onError: () => {
			handleResetForm();
			notificationStore.setNotification(standardError);
		},
		refetchQueries: ['GetCrewMember'],
		fetchPolicy: 'network-only',
	});

	const createData = () => {
		const { from, fromTime, cityId, note } = getValues();
		// if your LocationSearch returns timezone on cityId, keep passing it
		const newFrom = updateDateWithTime(from, fromTime, true, cityId?.timezone);

		return {
			input: {
				crewMemberId: employeeId,
				cityId: cityId.key ?? cityId.value, // support both shapes
				effectiveTimestamp: resetTimestampToUtc(newFrom),
				...(note ? { note } : {}),
			},
		};
	};

	const handleLocationChange = () => {
		const variables = createData();
		addLocationChange({ variables });
	};

	const handleResetForm = () => {
		reset(DEFAULT_VALUES);
	};

	const closeAndReset = () => {
		handleResetForm();
		onClose();
	};

	const watchedFrom = watch('from');
	const watchedFromTime = watch('fromTime');
	const watchedCityId = watch('cityId');
	const isValidForm = watchedFrom && watchedCityId && watchedFromTime;

	return (
		<Popup
			extraClasses={styles.modalWrapper}
			id="modal-update-crew-location"
			active
			onOverlayClick={closeAndReset}
		>
			<PopupSection type="title">
				{MODAL_UPDATE_CREW_LOCATION_TEXT.updateCrewLocationTitle}
			</PopupSection>

			<PopupSection extraClasses={styles.antiScroll} type="content">
				<Text extraClasses={styles.margin}>
					{MODAL_UPDATE_CREW_LOCATION_TEXT.updateCrewLocationDescription}
				</Text>

				<form onSubmit={handleSubmit(handleLocationChange)}>
					<div className={`${styles.row} ${styles.full}`}>
						<Controller
							name="cityId"
							control={control}
							rules={{ required: 'Location is required' }}
							render={({ field }) => (
								<LocationSearch
									fieldControl={field}
									setValue={setValue}
									fieldToReset="cityId"
									label={MODAL_UPDATE_CREW_LOCATION_TEXT.selectLocation}
									searchOnlyWithStations
								/>
							)}
						/>
					</div>

					<Grid align="bottom" justify="space-between">
						<GridCol size={9}>
							<Controller
								name="from"
								control={control}
								rules={{ required: 'Date is required' }}
								render={({ field }) => (
									<Input
										{...field}
										id="modal-update-crew-location-from-input"
										type="date"
										name="from"
										label={MODAL_UPDATE_CREW_LOCATION_TEXT.dateAndTimeAvailability}
										onChange={(e) => {
											field.onChange(e.target.value);
											setValue('fromTime', DEFAULT_FROM_TIME_VALUE, { shouldDirty: true });
										}}
									/>
								)}
							/>
						</GridCol>
						<GridCol size={3}>
							<Controller
								name="fromTime"
								control={control}
								rules={{ required: 'Time is required' }}
								disabled={getValues().from === ''}
								render={({ field }) => (
									<Input
										{...field}
										ref={null}
										id="from-time"
										name="fromTime"
										type="time"
										onChange={(e) => field.onChange(e.target.value)}
									/>
								)}
							/>
						</GridCol>
					</Grid>

					<div className={`${styles.row} ${styles.full} ${styles.note}`}>
						<Controller
							name="note"
							control={control}
							render={({ field }) => (
								<Textarea {...field} label={MODAL_UPDATE_CREW_LOCATION_TEXT.note} placeholder="" />
							)}
						/>
					</div>
				</form>
			</PopupSection>

			<PopupSection type="actions" extraClasses={styles.modalUpdateLocationActionsWrapper}>
				<Button onClick={closeAndReset}>
					<strong>{MODAL_UPDATE_CREW_LOCATION_TEXT.cancelBtn}</strong>
				</Button>
				<Button
					appearance="primary"
					disabled={!Object.keys(dirtyFields).length || !isValid || !isValidForm}
					type="submit"
					onClick={handleLocationChange}
					loading={loading}
				>
					<strong>{MODAL_UPDATE_CREW_LOCATION_TEXT.confirmBtn}</strong>
				</Button>
			</PopupSection>
		</Popup>
	);
};

export default ModalUpdateCrewLocation;
